import { createContext, useContext, useRef } from "react";
import { ActionSignatureModal } from "../modals/ActionSignatureModal";
import { useModalContext } from "ui/providers/ModalManager";
import { DISABLE_PASSWORD_ACTION } from "app/config";
interface Context {
  showActionSignatureModal: (options: {
    title: string;
    description: string;
  }) => Promise<{ password: string }>;

  showSignOffSignatureModal: (options: {
    title: string;
    description: string;
    editableEmail?: boolean;
  }) => Promise<{ email: string; password: string }>;
}
interface Props {
  children: React.ReactNode;
}
const ActionSignatureContext = createContext<Context>({} as Context);

export function ActionSignatureProvider({ children }: Props) {
  const callbackRef = useRef<any>();
  const { addModal, removeModal } = useModalContext();

  const showActionSignatureModal = (options) => {
    if (DISABLE_PASSWORD_ACTION) return Promise.resolve({ password: "" });
    return new Promise<{ password: string }>((resolve, reject) => {
      callbackRef.current = (password) => {
        if (password) {
          resolve({ password });
        } else {
          reject();
        }
      };

      addModal(
        <ActionSignatureModal
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          title={options.title}
          description={options.description}
        />,
      );
    });
  };

  const showSignOffSignatureModal = (options) => {
    return new Promise<{ email: string; password: string }>(
      (resolve, reject) => {
        callbackRef.current = ({ email, password }) => {
          if (password) {
            resolve({ email, password });
          } else {
            reject();
          }
        };

        addModal(
          <ActionSignatureModal
            title={options.title}
            description={options.description}
            editableEmail={options.editableEmail}
            onSuccess={({ email, password }) => {
              closeModal();
              callbackRef.current?.({ email, password });
            }}
            onCancel={() => {
              closeModal();
              callbackRef.current?.({ email: "", password: "" });
            }}
          />,
        );
      },
    );
  };

  const closeModal = () => {
    removeModal();
  };
  const handleSuccess = ({ password }: { password: string }) => {
    closeModal();
    callbackRef.current?.(password);
  };
  const handleCancel = () => {
    closeModal();
    callbackRef.current?.("");
  };

  return (
    <ActionSignatureContext.Provider
      value={{
        showActionSignatureModal,
        showSignOffSignatureModal,
      }}
    >
      {children}
    </ActionSignatureContext.Provider>
  );
}
export function useActionSignatureContext() {
  const context = useContext(ActionSignatureContext);
  if (!context) {
    throw new Error(
      "ActionSignatureContext must be used within a ActionSignatureProvider",
    );
  }
  return context;
}
